(ns hai.admin-ui.profile
  "User profile page: account info and personal settings."
  (:require
    [clojure.string :as str]
    [hai.library.connections.setup :as setup]
    [hai.ui.components :as c]
    [hai.ui.icons :as icons]
    [hai.ui.rf-sugar :refer [<s >e]]
    [hai.ui.tokens :as t]
    [hai.web-app.auth :as auth]
    [hai.web-app.feature-flag.ui :as ff-ui]
    [hai.web-app.layout :as layout]
    [hai.web-app.routing :as routing]
    [lambdaisland.ornament :as o]
    [reagent.core :as r])
  (:require-macros
   [hai.web-app.macros :as macros]))

(def ^:private app-version (macros/version))

;; ── Profile card ──────────────────────────────────────────────────────────────

(o/defstyled profile-card :div
  :flex :flex-col
  {:background-color t/--colour-snow
   :border-radius "12px"
   :gap "12px"
   :padding "20px"
   :border "1px solid var(--colour-rock)"
   :max-width "420px"})

(o/defstyled profile-identity-row :div
  :flex :items-center {:gap "12px"})

(o/defstyled profile-avatar :div
  :flex :items-center :justify-center :flex-shrink-0
  {:width "44px"
   :height "44px"
   :border-radius "50%"
   :background-color t/--colour-chalk
   :color t/--text-secondary-50
   :font-size "15px"
   :font-weight "700"
   :letter-spacing "0.03em"
   :user-select "none"})

(o/defstyled profile-name :div
  {:font-size "16px" :font-weight "600" :color t/--text-secondary})

(o/defstyled profile-field-row :div
  :flex :items-center {:gap "8px" :min-height "24px"}
  [:svg {:width "14px" :height "14px" :flex-shrink "0" :opacity "0.5"}])

(o/defstyled profile-field-text :span
  {:font-size "13px" :color t/--text-secondary :opacity "0.75"})

(o/defstyled version-label :span
  {:position "fixed"
   :bottom "1rem"
   :color t/--text-secondary-60
   :font-size "12px"
   :transition "left 0.1s ease"}
  [:at-media {:max-width "768px"}
   {:bottom "0" :left "0 !important"}]
  ([& children]
   (let [open?     @(<s :nav/navbar-open?)
         nav-width (if open? layout/navbar-width layout/navbar-collapsed-width)]
     (into [:<> {:style {:left (str (+ nav-width 16) "px")}}] children))))

(defn- full-name-field []
  (r/with-let [!identity    (<s :auth/identity)
               !editing?    (r/atom false)
               !draft       (r/atom "")]
    (let [{:user/keys [uuid full-name]} @!identity
          save!       (fn []
                        (>e [:resolve
                             `[(~'user/update
                                ~{:user/uuid uuid
                                  :user/full-name @!draft})]
                             {:on-success-fx [[:dispatch [:auth/init]]]}])
                        (reset! !editing? false))]
      (if @!editing?
        [c/Flex {:gap "2" :align "center"}
         [c/TextFieldRoot
          ;; Uncontrolled (`:defaultValue`, not `:value`): `c/TextFieldRoot` is a Radix
          ;; component, so Reagent can't apply its controlled-input caret-preservation
          ;; workaround and a controlled `:value` snaps the cursor to the end on every
          ;; keystroke. `!draft` is seeded before this branch mounts and kept in sync by
          ;; `:on-change` for `save!`, so the DOM owning the value is fine.
          {:defaultValue @!draft
           :autoFocus true
           :placeholder "Your name"
           :on-change #(reset! !draft (.. % -target -value))
           :on-key-down (fn [e]
                          (when (= (.-key e) "Enter")
                            (save!)))}]
         [c/Button {:size "1" :on-click save!} "Save"]
         [c/Button {:size "1" :variant "soft"
                    :on-click #(reset! !editing? false)}
          "Cancel"]]
        [c/Flex {:gap "2" :align "center"}
         [profile-name (or full-name "—")]
         [c/Button {:size "1" :variant "ghost"
                    :on-click (fn []
                                (reset! !draft (or full-name ""))
                                (reset! !editing? true))}
          "Edit"]]))))

(defn- github-username-field []
  (r/with-let [!identity    (<s :auth/identity)
               !editing?    (r/atom false)
               !draft       (r/atom "")]
    (let [{:user/keys [uuid]} @!identity
          gh-username (:user-config.timeline.github/my-actions-filter-username @!identity)
          save!       (fn []
                        (>e [:resolve
                             `[(~'user/update
                                ~{:user/uuid uuid
                                  :user-config.timeline.github/my-actions-filter-username @!draft})]
                             {:on-success-fx [[:dispatch [:auth/init]]]}])
                        (reset! !editing? false))]
      [profile-field-row
       [icons/github {:size 14}]
       (if @!editing?
         [c/Flex {:gap "2" :align "center"}
          [c/TextFieldRoot
           ;; Uncontrolled — see the note in `full-name-field`. A controlled `:value` on
           ;; this Radix input snaps the cursor to the end each keystroke.
           {:defaultValue @!draft
            :autoFocus true
            :placeholder "e.g. octocat"
            :on-change #(reset! !draft (.. % -target -value))
            :on-key-down (fn [e]
                           (when (= (.-key e) "Enter")
                             (save!)))}]
          [c/Button {:size "1" :on-click save!} "Save"]
          [c/Button {:size "1" :variant "soft"
                     :on-click #(reset! !editing? false)}
           "Cancel"]]
         [c/Flex {:gap "2" :align "center"}
          [profile-field-text (or gh-username "\u2014")]
          [c/Button {:size "1" :variant "ghost"
                     :on-click (fn []
                                 (reset! !draft (or gh-username ""))
                                 (reset! !editing? true))}
           "Edit"]])])))

(defn- sign-out-confirmation
  "Radix AlertDialog for the sign-out flow."
  [!open?]
  [c/AlertDialogRoot
   {:open @!open?
    :onOpenChange #(reset! !open? %)}
   [c/AlertDialogContent
    [c/AlertDialogTitle "Sign out"]
    [c/AlertDialogDescription
     "Are you sure you want to sign out?"]
    [c/Flex {:justify "end" :gap "3" :mt "4"}
     [c/AlertDialogCancel
      [c/Button {:variant "soft"} "Cancel"]]
     [c/AlertDialogAction
      [c/Button {:variant "soft"
                 :color "red"
                 :on-click #(>e [:auth/logout])}
       "Sign out"]]]]])

(defn- sign-out-button []
  (r/with-let [!open? (r/atom false)]
    [:<>
     [c/Button {:variant "soft" :color "red"
                :on-click #(reset! !open? true)}
      [icons/outlined-power_settings_new]
      "Sign out"]
     [sign-out-confirmation !open?]]))

(defn- newtab-override-field []
  (let [override? (boolean (:user-config.newtab/override? @(<s :auth/identity)))]
    [c/DataListItem
     [c/DataListLabel "New tab page"]
     [c/DataListValue
      [c/Flex {:gap "3" :align "center"}
       [c/Switch {:checked override?
                  :on-checked-change #(>e [::auth/set-newtab-override %])}]
       [c/Text {:size "2" :color "gray"}
        "Replace your browser's new tab with Humble. Chrome only for now."]]]]))

(defn profile-page
  [_]
  [layout/page
   [layout/breadcrumbs]
   [layout/heading]
   [layout/lead
    "Make Humble yours. Control how you appear, your preferences and settings."]
   [layout/content
    (let [{:user/keys [email full-name display-name]} @(<s :auth/identity)
          person-name (or full-name display-name)]
      [profile-card
       [profile-identity-row
        [profile-avatar (if (seq person-name) (layout/initials person-name) [icons/outlined-person])]
        [full-name-field]]
       [profile-field-row [icons/outlined-email] [profile-field-text (str/join ", " email)]]
       [github-username-field]])
    [setup/where-you-work]
    [newtab-override-field]


    [:div {:style {:display "flex" :flex-direction "column" :gap "20px"
                   :margin-top "20px"}}
     [c/Link {:class "profile-link" :href (routing/href :connections/table) :underline "none" :color "gray"
              :aria-label "My profile"}
      [c/Flex {:align "center" :gap "2"}
       [icons/outlined-electrical_services]
       "Connections"]]
     [ff-ui/feature-flags-panel]
     [c/Flex [sign-out-button]]]]
   [version-label (str "v " (:minor app-version))]])

(defn routes []
  [["/profile" {:name :admin-ui/profile
                :label "My Profile"
                :icon icons/outlined-person
                ::routing/window-title "Profile"
                :view profile-page}]])
